<script setup lang="ts">
type TaskData = App.Tasks.Data.TaskData;

import ClipboardNotes from '@/icons/line/clipboard-notes.svg';
import UsdCircle from '@/icons/line/usd-circle.svg';
import File from '@/icons/line/file.svg';
import University from '@/icons/line/university.svg';
import AwardAlt from '@/icons/line/award-alt.svg';
import Clock from '@/icons/line/clock.svg';
import Check from '@/icons/line/check.svg';
import Times from '@/icons/line/times.svg';

const props = defineProps<{
  task: TaskData;
}>();

type TaskIcon = {
  icon: Component;
  wrapperStyle: string;
  iconStyle: string;
};

const taskIcon = computed((): TaskIcon => {
  const statusIconSize = 'h-full w-full';
  switch (props.task.status.value) {
    case 'pending':
      return {
        icon: Clock,
        wrapperStyle: 'border-amber-300 bg-amber-100',
        iconStyle: `text-amber-700 ${statusIconSize}`
      };
    case 'completed':
      return {
        icon: Check,
        wrapperStyle: 'border-secondary-900 bg-secondary-900',
        iconStyle: `text-white ${statusIconSize}`
      };
    case 'dismissed':
      return {
        icon: Times,
        wrapperStyle: 'border-slate-300 bg-slate-100',
        iconStyle: `text-slate-700 ${statusIconSize}`
      };
    case 'rejection_dismissed':
      return {
        icon: Times,
        wrapperStyle: 'border-slate-300 bg-slate-100',
        iconStyle: `text-slate-700 ${statusIconSize}`
      };
  }

  let icon;
  const taskIconSize = 'h-3 w-3';

  switch (props.task.type.value) {
    case 'survey':
      icon = ClipboardNotes;
      break;
    case 'college_application':
      icon = University;
      break;
    case 'financial_aid':
      icon = UsdCircle;
      break;
    case 'scholarship':
      icon = AwardAlt;
      break;
    case 'test':
      icon = File;
      break;
    default:
      icon = undefined;
      break;
  }

  switch (true) {
    // rejected
    case props.task.status.value === 'rejected':
      icon = icon ? icon : Times;
      return {
        icon,
        wrapperStyle: 'border-red-300 bg-red-100',
        iconStyle: `text-red-700 ${taskIconSize}`
      };
    // overdue
    case props.task.due_date && props.task.due_date < new Date().toISOString():
      return {
        icon,
        wrapperStyle: 'border-red-300 bg-red-100',
        iconStyle: `text-red-700 ${taskIconSize}`
      };
    // due in next 7 days
    case props.task.due_date &&
      props.task.due_date < new Date(new Date().setDate(new Date().getDate() + 7)).toISOString():
      return {
        icon,
        wrapperStyle: 'border-primary-300 bg-primary-100',
        iconStyle: `text-primary-700 ${taskIconSize}`
      };
    default:
      return {
        icon,
        wrapperStyle: 'border-secondary-300 bg-secondary-100',
        iconStyle: `text-secondary-700 ${taskIconSize}`
      };
  }
});
</script>

<template>
  <div
    v-if="taskIcon?.icon"
    class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border"
    :class="taskIcon?.wrapperStyle"
  >
    <component :is="taskIcon?.icon" :class="taskIcon?.iconStyle" />
  </div>
</template>
